<script setup lang="ts">
import { ref, computed } from "vue";
import { useLoginStore } from "@/modules/login/store/login_store";
import { loginService } from "@/shared/services/login_service";
import { useRoute, useRouter } from "vue-router";
import { useDetalhesProposta } from "@/modules/detalhes_proposta/store/detalhes_proposta_store";

type MenuChild = {
  label: string;
  routeName: string;
};

type MenuItem = {
  label: string;
  routeName?: string;
  children?: MenuChild[];
};

const menu = ref(false);
const drawer = ref(false);
const loginStore = useLoginStore();
const detalhesPropostaStore = useDetalhesProposta();
const route = useRoute();
const router = useRouter();

function logout() {
  loginService.logout();
}

const pageTitle = computed(() => {
  if (route.name === "DetalhesProposta") {
    return detalhesPropostaStore.currentStepName;
  }

  if (route.name === "Financiamentos") {
    return loginStore.authUser?.data?.profileSlug === "rca"
      ? "Portal do RCA"
      : "Portal do Integrador";
  }

  return (route.meta?.title as string) ?? "";
});

const isActiveRoute = (routeName: string) => {
  return route.name === routeName;
};

const isActiveMenu = (item: MenuItem) => {
  if (item.children) {
    return item.children.some((child) => route.name === child.routeName);
  }

  return route.name === item.routeName;
};

const navigateTo = (routeName: string) => {
  if (routeName === "BackofficeFinanceiroListagemBoletos") {
    router.push({
      name: routeName,
      params: { id: loginStore.authUser?.data?.userId ?? 0 },
    });
    return;
  }

  router.push({ name: routeName });
  drawer.value = false;
};

// Menus baseados no perfil
const menuItems = computed<MenuItem[]>(() => {
  const profileSlug = loginStore.authUser?.data?.profileSlug ?? "";

  switch (profileSlug) {
    case "Cliente":
      return [{ label: "Pagamentos", routeName: "PagamentosCliente" }];

    case "Integrador":
      return [
        { label: "Financiamentos", routeName: "Financiamentos" },
        { label: "Contas", routeName: "Contas" },
        { label: "Pagamentos", routeName: "PagamentosCliente" },
        { label: "Meus Dados", routeName: "MeusDados" },
      ];

    case "Adm-Integrador":
      return [
        { label: "Pagamentos", routeName: "PagamentosCliente" },
        {
          label: "Minha Empresa",
          children: [
            { label: "Funcionários", routeName: "" },
            { label: "Meus Dados", routeName: "MeusDados" },
          ],
        },
        {
          label: "Configurações",
          children: [{ label: "Clientes", routeName: "cadastroCliente" }],
        },
      ];

    case "BackOffice-Engenharia":
      return [
        { label: "Dashboard", routeName: "Dashboard" },
        { label: "Análises", routeName: "BackofficeEngenharia" },
      ];

    case "BackOffice-Comercial":
      return [
        { label: "Dashboard", routeName: "Dashboard" },
        { label: "Integradores", routeName: "BackofficeComercial" },
        {
          label: "Financiamentos Reprovados",
          routeName: "FinanciamentosReprovados",
        },
      ];

    case "BackOffice-Financeiro":
      return [
        { label: "Dashboard", routeName: "Dashboard" },
        {
          label: "Boletos",
          routeName: "BackofficeFinanceiroListagemBoletos",
        },
      ];

    case "Administrador":
      // Admin tem acesso a todos os menus
      return [
        { label: "Geral", routeName: "Financiamentos" },
        {
          label: "Configurações",
          children: [
            { label: "Cliente", routeName: "cadastroCliente" },
            { label: "Cadastro de Perfis", routeName: "cadastroPerfil" },
            { label: "Usuários", routeName: "CadastroUsuarios" },
            { label: "Categorias", routeName: "CategoriaIntegradores" },
            { label: "Produtos Bancários", routeName: "ProdutosBancarios" },
            {
              label: "Produtos Relacionados",
              routeName: "ProdutosRelacionados",
            },
          ],
        },
        // { label: "Backoffice Engenharia", routeName: "BackofficeEngenharia" },
        // { label: "Backoffice Comercial", routeName: "BackofficeComercial" },
      ];

    default:
      return [];
  }
});
</script>

<template>
  <v-app-bar flat height="72" color="white" class="px-6 navbar">
    <v-row>
      <!-- Hamburguer (mobile) -->
      <v-col cols="auto" class="d-flex d-md-none align-center">
        <v-btn
          icon="mdi-menu"
          variant="text"
          size="large"
          @click="drawer = !drawer"
        />
      </v-col>

      <!-- Logo (desktop) -->
      <v-col cols="auto" class="d-none d-md-flex align-center">
        <img
          src="@/assets/LOGO-Liberta-Financiamentos.svg"
          alt="Liberta Financiamentos"
          class="logo"
        />
      </v-col>

      <!-- Menu (desktop) -->
      <v-col cols="auto" class="d-none d-md-flex align-center ga-6 menu-links">
        <template v-for="item in menuItems" :key="item.label">
          <v-menu v-if="item.children" location="bottom start">
            <template #activator="{ props }">
              <span
                class="menu-item d-flex align-center ga-1"
                :class="{ active: isActiveMenu(item) }"
                v-bind="props"
              >
                {{ item.label }}
                <v-icon size="18" color="orange-darken-2">
                  mdi-chevron-down
                </v-icon>
              </span>
            </template>

            <v-sheet elevation="6" class="menu-sheet">
              <v-list density="compact">
                <template
                  v-for="(child, index) in item.children"
                  :key="child.routeName"
                >
                  <v-list-item
                    :disabled="!child.routeName"
                    @click="child.routeName && navigateTo(child.routeName)"
                  >
                    <v-list-item-title>{{ child.label }}</v-list-item-title>
                  </v-list-item>

                  <v-divider v-if="index < item.children.length - 1" />
                </template>
              </v-list>
            </v-sheet>
          </v-menu>

          <span
            v-else-if="item.routeName"
            class="menu-item"
            :class="{ active: isActiveRoute(item.routeName) }"
            @click="navigateTo(item.routeName)"
          >
            {{ item.label }}
          </span>
        </template>
      </v-col>

      <!-- Título central -->
      <v-col class="d-flex justify-center" cols>
        <span class="page-title">{{ pageTitle }}</span>
      </v-col>

      <!-- Usuário (desktop) -->
      <v-col cols="auto" class="d-none d-md-flex justify-end">
        <v-menu
          v-model="menu"
          transition="fade-transition"
          location="bottom end"
          :close-on-content-click="true"
        >
          <template #activator="{ props }">
            <div class="d-flex align-center ga-3 user-activator" v-bind="props">
              <v-icon size="28" color="orange-darken-3">
                mdi-account-circle-outline
              </v-icon>
              <div class="d-flex flex-column text-right">
                <span class="welcome">Boas-vindas</span>
                <span class="user-name">
                  {{ loginStore.usuarioLogado?.nome ?? "" }}
                </span>
              </div>
              <v-icon size="18">mdi-chevron-down</v-icon>
            </div>
          </template>

          <v-sheet elevation="6" class="menu-sheet">
            <v-list density="compact">
              <v-list-item @click="navigateTo('CadastroUsuarios')">
                <v-list-item-title>Usuários</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item>
                <v-list-item-title>Perfis</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="logout" class="sair-item">
                <v-list-item-title>Sair</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-sheet>
        </v-menu>
      </v-col>
    </v-row>
  </v-app-bar>

  <!-- Drawer (mobile) -->
  <v-navigation-drawer
    v-model="drawer"
    temporary
    location="left"
    width="280"
    class="d-md-none mobile-drawer"
  >
    <div class="pa-6 d-flex justify-center">
      <img
        src="@/assets/LOGO-Liberta-Financiamentos.svg"
        alt="Liberta Financiamentos"
        class="drawer-logo"
      />
    </div>

    <v-divider />

    <v-list nav density="comfortable">
      <template v-for="item in menuItems" :key="item.label">
        <v-list-group v-if="item.children" :value="item.label">
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              :title="item.label"
              :active="isActiveMenu(item)"
              class="drawer-item"
            />
          </template>

          <v-list-item
            v-for="child in item.children"
            :key="child.routeName"
            :title="child.label"
            :active="isActiveRoute(child.routeName)"
            class="drawer-item"
            @click="navigateTo(child.routeName)"
          />
        </v-list-group>

        <v-list-item
          v-else-if="item.routeName"
          :title="item.label"
          :active="isActiveRoute(item.routeName)"
          class="drawer-item"
          @click="navigateTo(item.routeName)"
        />
      </template>
    </v-list>

    <template #append>
      <v-divider />
      <v-list nav density="comfortable">
        <v-list-item
          title="Sair"
          class="drawer-item drawer-logout"
          @click="logout"
        />
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<style scoped>
.navbar {
  border-bottom: 1px solid #e0e0e0;
  height: 96px;
  display: flex;
  justify-content: center;
}
.logo {
  width: 111px;
}
.menu-links {
  margin-left: 32px;
}
.menu-item {
  font-size: 16px;
  font-weight: 500;
  color: #2c2c2c;
  cursor: pointer;
  position: relative;
  padding-right: 16px;
  border-right: 1px solid #cfcfcf;
  font-family: "Qubic Grid", sans-serif;
}

/* Drawer (mobile) */
.drawer-logo {
  width: 130px;
}
.drawer-item {
  font-size: 16px;
  color: #2c2c2c;
}
.drawer-item.v-list-item--active {
  font-weight: 600;
  color: #f57c00;
}
.drawer-logout :deep(.v-list-item-title) {
  color: #f57c00;
  font-weight: 600;
}

/* Drawer cobre toda a altura, inclusive sobre a navbar */
.mobile-drawer {
  top: 0 !important;
  height: 100dvh !important;
  max-height: 100dvh !important;
  z-index: 2000 !important;
}

/* Mobile - ajustes da navbar */
@media (max-width: 959px) {
  .navbar {
    height: 64px !important;
    padding: 0 12px !important;
  }
  .navbar :deep(.v-toolbar__content) {
    height: 64px !important;
    align-items: center;
  }
  .navbar .w-100 {
    position: relative;
    height: 64px;
  }
  .page-title {
    font-size: 18px;
    font-weight: 700;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}
.menu-item.active {
  font-weight: 600;
}
.menu-item.active::after {
  content: "";
  position: absolute;
  bottom: -6px;
  left: 2px;
  right: 16px;
  height: 2px;
  background-color: #f57c00;
  border-radius: 2px;
}
.page-title {
  font-size: 24px;
  font-weight: 500;
  color: #2c2c2c;
  font-family: "DM Sans", sans-serif;
}
.user-activator {
  cursor: pointer;
}
.welcome {
  font-size: 12px;
  color: #7a7a7a;
}
.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c2c2c;
}
.menu-sheet {
  width: 200px;
  border-radius: 6px;
  background: white;
}
.menu-sheet :deep(.v-list-item-title) {
  font-size: 14px;
  font-weight: bold;
}
.v-list-item {
  height: 48px;
  align-items: center;
  padding-left: 16px;
  cursor: pointer;
}
.v-divider {
  margin: 0;
}
.sair-item .v-list-item-title {
  color: #f57c00;
  font-weight: 600;
}
</style>
